import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PageLoading } from '../common/LoadingSpinner';
import { ROUTES, USER_TYPES } from '../../constants/constants';
import config from '../../config/config';
import { OrgProvider } from '../../contexts/OrgContext';
import { confirmAppReady } from '../../capacitor/nativeInit';

// Route components (lazy loaded for code splitting)
import PrivateRoute from './PrivateRoute';
import TeamManagementRoute from './TeamManagementRoute';
import PublicRoute from './PublicRoute';
import Layout from '../layout/Layout';
import DashboardRedirect from './DashboardRedirect';

// Lazy load pages
const Login = React.lazy(() => import('../../pages/auth/Login'));
const ForgotPassword = React.lazy(() => import('../../pages/auth/ForgotPassword'));
const ResetPassword = React.lazy(() => import('../../pages/auth/ResetPassword'));

const Dashboard = React.lazy(() => import('../../pages/Dashboard'));
const FreeSearchTrademark = React.lazy(() => import('../../pages/search/FreeSearchTrademark'));
const Search = React.lazy(() => import('../../pages/search/Search'));
const SearchHistory = React.lazy(() => import('../../pages/search/SearchHistory'));
const SearchStatistics = React.lazy(() => import('../../pages/search/SearchStatistics'));

// TM Manager Pages
const YourTrademarkDashboard = React.lazy(() => import('../../pages/dashboard/YourTrademarkDashboard'));
const YourDeadlineManagement = React.lazy(() => import('../../pages/dashboard/YourDeadlineManagement'));
const LegalDraftingPage = React.lazy(() => import('../../pages/legal/LegalDraftingPage'));
const CaseLibraryPage      = React.lazy(() => import('../../pages/legal/CaseLibraryPage'));
const BrandGeneratorPage   = React.lazy(() => import('../../pages/brand/Brandgeneratorpage'));
const TrademarkPredictorPage = React.lazy(() => import('../../pages/brand/TrademarkPredictorPage'));
const SupportPage          = React.lazy(() => import('../../pages/support/Supportpage'));
const YourAllTrademark = React.lazy(() => import('../../pages/dashboard/YourAllTrademark'));
const PortfolioDeadlineManagement = React.lazy(() => import('../../pages/dashboard/PortfolioDeadlineManagement'));
const PortfolioTrademarks = React.lazy(() => import('../../pages/dashboard/PortfolioTrademarks'));
const ManageTrademark = React.lazy(() => import('../../pages/dashboard/ManageTrademark'));

const OpponentTrademarkDashboard = React.lazy(() => import('../../pages/dashboard/OpponentTrademarkDashboard'));
const OpponentDeadlineManagement = React.lazy(() => import('../../pages/dashboard/OpponentDeadlineManagement'));
const OpponentTrademark = React.lazy(() => import('../../pages/dashboard/OpponentAllTrademark'));
const Portfolio = React.lazy(() => import('../../pages/dashboard/Portfolio'));


const ExaminationReport = React.lazy(() => import('../../pages/dashboard/ExaminationReport'));
const ProprietorStatus = React.lazy(() => import('../../pages/dashboard/ProprietorStatus'));
const ApplicationStatus = React.lazy(() => import('../../pages/dashboard/ApplicationStatus'));
const ApplicationLevel = React.lazy(() => import('../../pages/dashboard/ApplicationLevel'));
const ChangeTracker = React.lazy(() => import('../../pages/dashboard/ChangeTracker'));
const StatusChangeTracker = React.lazy(() => import('../../pages/dashboard/StatusChangeTracker'));
const DocumentChangeTracker = React.lazy(() => import('../../pages/dashboard/DocumentChangeTracker'));
const AttorneyChangeTracker = React.lazy(() => import('../../pages/dashboard/AttorneyChangeTracker'));
const UploadedDocumentTracker = React.lazy(() => import('../../pages/dashboard/UploadedDocumentTracker'));

const JournalLevel = React.lazy(() => import('../../pages/dashboard/JournalLevel'));
const SingleClickReport = React.lazy(() => import('../../pages/dashboard/SingleClickReport'));
const FocusWord = React.lazy(() => import('../../pages/dashboard/FocusWord'));
const AstroMark = React.lazy(() => import('../../pages/dashboard/AstroMark'));
const ApplyingTrademark = React.lazy(() => import('../../pages/dashboard/ApplyingTrademark'));
const ManageCustomer = React.lazy(() => import('../../pages/dashboard/ManageCustomer'));
const ImageConverter = React.lazy(() => import('../../pages/dashboard/ImageConverter'));
const ManageTags = React.lazy(() => import('../../pages/dashboard/ManageTags'));


const Profile = React.lazy(() => import('../../pages/profile/Profile'));
const Preferences = React.lazy(() => import('../../pages/profile/Preferences'));
const WhatsAppSetup = React.lazy(() => import('../../pages/settings/WhatsAppSetup'));
const EmailSettings = React.lazy(() => import('../../pages/settings/EmailSettings'));
const ClientEmailTemplate = React.lazy(() => import('../../pages/settings/ClientEmailTemplate'));

const NotificationAnalytics = React.lazy(() => import('../../pages/dashboard/NotificationAnalytics'));
const AstroMarkReport = React.lazy(() => import('../../pages/All_Reports/AstroMarkReport'));
const SearchReport = React.lazy(() => import('../../pages/All_Reports/SearchReport'));
const Users = React.lazy(() => import('../../pages/users/Users'));
const UserCreate = React.lazy(() => import('../../pages/users/UserCreate'));
const UserEdit = React.lazy(() => import('../../pages/users/UserEdit'));

// Client Admin Panel
const ClientAdminDashboard = React.lazy(() => import('../../pages/admin/ClientAdminDashboard'));
const RolesManager = React.lazy(() => import('../../pages/admin/RolesManager'));
const AdminUsers = React.lazy(() => import('../../pages/admin/AdminUsers'));
const TeamManagement = React.lazy(() => import('../../pages/admin/TeamManagement'));
const HierarchyView  = React.lazy(() => import('../../pages/admin/HierarchyView'));

// Task Management
const TaskBoard = React.lazy(() => import('../../pages/tasks/TaskBoard'));
const RemindersListPage = React.lazy(() => import('../../pages/tasks/RemindersListPage'));

const Reports = React.lazy(() => import('../../pages/reports/Reports'));
const ReportCreate = React.lazy(() => import('../../pages/reports/ReportCreate'));
const ReportView = React.lazy(() => import('../../pages/reports/ReportView'));

const Documents = React.lazy(() => import('../../pages/documents/Documents'));
const Notes = React.lazy(() => import('../../pages/notes/Notes'));

const Settings = React.lazy(() => import('../../pages/settings/Settings'));
const Billing = React.lazy(() => import('../../pages/billing/Billing'));
const OpponentStrengthMeter = React.lazy(() => import('../../pages/dashboard/Opponentstrengthmeter'));
const License = React.lazy(() => import('../../pages/license/License'));
const DesignSearch = React.lazy(() => import('../../pages/search/DesignSearch'));
const PatentSearch = React.lazy(() => import('../../pages/search/PatentSearch'));
const InternationalSearch = React.lazy(() => import('../../pages/search/InternationalSearch'));
const NotFound = React.lazy(() => import('../../pages/NotFound'));
const Unauthorized = React.lazy(() => import('../../pages/Unauthorized'));

const AppRouter = () => {
  const { isLoading } = useAuth();

  // Once the initial auth state resolves, the app has reached a known-good
  // state — tell Capgo the OTA bundle booted OK (no-op on web).
  useEffect(() => {
    if (!isLoading) {
      confirmAppReady();
    }
  }, [isLoading]);

  if (isLoading) {
    return <PageLoading message="Initializing application..." />;
  }

  return (
    <Router basename={config.publicPath}>
      <Suspense fallback={<PageLoading />}>
        <Routes>


           <Route
            path="/free-trademark-search"
            element={
              <PublicRoute>
                <FreeSearchTrademark />
              </PublicRoute>
            }
            
          />
          {/* Public Routes */}
          <Route path={ROUTES.LOGIN} element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } />

          <Route path={ROUTES.FORGOT_PASSWORD} element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          } />

          <Route path={ROUTES.RESET_PASSWORD} element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          } />


          {/* Private Routes with Layout */}
          <Route path="/" element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }>
            {/* Dashboard - Default route */}
            <Route index element={<Navigate to={ROUTES.DASHBOARD} replace />} />
            <Route
              path={ROUTES.DASHBOARD}
              element={
                <PrivateRoute>
                  <DashboardRedirect />
                </PrivateRoute>
              }
            />


            {/* <Route path={ROUTES.DASHBOARD} element={<Dashboard />} /> */}
            {/* <Route path={ROUTES.DASHBOARD} element={<SearchStatistics />} /> */}

            {/* <Route index element={<Navigate to={ROUTES.SEARCH_STATISTICS} replace />} />
            <Route path={ROUTES.SEARCH_STATISTICS} element={<SearchStatistics />} /> */}
            <Route
              path={ROUTES.Applying_Trademark}
              element={
                <PrivateRoute requiredPermissions={["Apply_trademark"]}>
                  <ApplyingTrademark />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.Manage_Customer}
              element={
                <PrivateRoute requiredPermissions={["Manage_Customer"]}>
                  <ManageCustomer />
                </PrivateRoute>
              }
            />

            {/* Search Routes */}
            <Route path={ROUTES.SEARCH} element={<Search />} />
            <Route
              path={ROUTES.DESIGN_SEARCH}
              element={
                <PrivateRoute
                  requiredPermissions={["Design_Search_Permission"]}
                >
                  <DesignSearch />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.PATENT_SEARCH}
              element={
                <PrivateRoute
                  requiredPermissions={["Patent_Search_Permission"]}
                >
                  <PatentSearch />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.INTERNATIONAL_SEARCH}
              element={
                <PrivateRoute
                  requiredPermissions={["International_Search_Permission"]}
                >
                  <InternationalSearch />
                </PrivateRoute>
              }
            />
            <Route path={ROUTES.SEARCH_HISTORY} element={<SearchHistory />} />
            <Route
              path={ROUTES.SEARCH_STATISTICS}
              element={
                <PrivateRoute requiredPermissions={["Reports"]}>
                  <SearchStatistics />
                </PrivateRoute>
              }
            />

            {/* TM Manager Routes */}
            <Route
              path={ROUTES.YOUR_TRADEMARK_DASHBOARD}
              element={
                <PrivateRoute requiredPermissions={["TM_Manager"]}>
                  <YourTrademarkDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.YOUR_DEADLINES_DASHBOARD}
              element={
                <PrivateRoute requiredPermissions={["TM_Manager"]}>
                  <YourDeadlineManagement />
                </PrivateRoute>
              }
            />

            <Route
              path={ROUTES.YOUR_ALL_TRADEMARK}
              element={
                <PrivateRoute requiredPermissions={["TM_Manager"]}>
                  <YourAllTrademark />
                </PrivateRoute>
              }
            />

            <Route
              path={ROUTES.OPPONENT_TRADEMARK_DASHBOARD}
              element={
                <PrivateRoute requiredPermissions={["TM_Manager"]}>
                  <OpponentTrademarkDashboard />
                </PrivateRoute>
              }
            />

            <Route
              path={ROUTES.OPPONENT_DEADLINES_DASHBOARD}
              element={
                <PrivateRoute requiredPermissions={["TM_Manager"]}>
                  <OpponentDeadlineManagement />
                </PrivateRoute>
              }
            />

            <Route
              path={ROUTES.OPPONENT_ALL_TRADEMARK}
              element={
                <PrivateRoute requiredPermissions={["TM_Manager"]}>
                  <OpponentTrademark />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.PORTFOLIO}
              element={
                <PrivateRoute requiredPermissions={["TM_Manager"]}>
                  <Portfolio />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.Portfolio_Deadline_Management}
              element={
                <PrivateRoute requiredPermissions={["TM_Manager"]}>
                  <PortfolioDeadlineManagement />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.Portfolio_Trademarks}
              element={
                <PrivateRoute requiredPermissions={["TM_Manager"]}>
                  <PortfolioTrademarks />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.MANAGE_TRADEMARKS}
              element={
                <PrivateRoute requiredPermissions={["TM_Manager"]}>
                  <ManageTrademark />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.Examination_Report}
              element={
                <PrivateRoute requiredPermissions={["Watch_Manager", "Examination_Level"]}>
                  <ExaminationReport />
                </PrivateRoute>
              }
            />

            <Route
              path={ROUTES.Application_Level}
              element={
                <PrivateRoute requiredPermissions={["Watch_Manager", "Application_Level"]}>
                  <ApplicationLevel />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.Application_Status}
              element={
                <PrivateRoute requiredPermissions={["Watch_Manager", "Application_Status"]}>
                  <ApplicationStatus />
                </PrivateRoute>
              }
            />

            <Route
              path={ROUTES.Status_Change_Tracker}
              element={
                <PrivateRoute requiredPermissions={["Watch_Manager", "Status_Change_Tracker"]}>
                  <StatusChangeTracker />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.Document_Change_Tracker}
              element={
                <PrivateRoute requiredPermissions={["Watch_Manager", "Status_Change_Tracker"]}>
                  <DocumentChangeTracker/>
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.Attorney_Change_Tracker}
              element={
                <PrivateRoute requiredPermissions={["Watch_Manager", "Status_Change_Tracker"]}>
                  <AttorneyChangeTracker/>
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.Upload_Document_Tracker}
              element={
                <PrivateRoute requiredPermissions={["Watch_Manager", "Status_Change_Tracker"]}>
                  <UploadedDocumentTracker/>
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.Change_Tracker}
              element={
                <PrivateRoute requiredPermissions={["Watch_Manager", "Status_Change_Tracker"]}>
                  <ChangeTracker />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.Journal_Level}
              element={
                <PrivateRoute requiredPermissions={["Watch_Manager", "Journal_Level"]}>
                  <JournalLevel />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.Single_Click_Report}
              element={
                <PrivateRoute requiredPermissions={["Premium_Features", "Single_Click_Report"]}>
                  <SingleClickReport />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.Focus_Word}
              element={
                <PrivateRoute requiredPermissions={["Premium_Features", "Focus_Word"]}>
                  <FocusWord />
                </PrivateRoute>
              }
            />
           <Route
              path={ROUTES.Proprietor_Status}
              element={
                <PrivateRoute requiredPermissions={["Premium_Features", "Proprietor_Status"]}>
                  <ProprietorStatus />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.Astro_Mark}
              element={
                <PrivateRoute requiredPermissions={["Premium_Features", "Astro_Mark"]}>
                  <AstroMark />
                </PrivateRoute>
              }
            />

            {/* Profile Routes */}
            <Route path={ROUTES.PROFILE} element={<Profile />} />
            <Route path={ROUTES.PREFERENCES} element={<Preferences />} />

            <Route
              path={ROUTES.WHATSAPP_SETUP}
              element={
                <PrivateRoute requiredPermissions={["WhatsApp_Search_Permission"]}>
                  <WhatsAppSetup />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.EMAIL_SETTINGS}
              element={
                <PrivateRoute requiredPermissions={["Client_Email_Setting"]}>
                  <EmailSettings />
                </PrivateRoute>
              }
            />

            <Route
              path={ROUTES.CLIENT_EMAIL_TEMPLATE}
              element={
                <PrivateRoute requiredPermissions={["Client_Email_Template"]}>
                  <ClientEmailTemplate />
                </PrivateRoute>
              }
            />

            <Route
              path={ROUTES.Notification_Analytics}
              element={
                <PrivateRoute requiredPermissions={["Reports"]}>
                  <NotificationAnalytics />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.Astro_Mark_Report}
              element={
                <PrivateRoute requiredPermissions={["Reports", "Astro_Mark"]}>
                  <AstroMarkReport />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.Search_Report}
              element={
                <PrivateRoute requiredPermissions={["Reports", "Search_Report"]}>
                  <SearchReport />
                </PrivateRoute>
              }
            />
            <Route path={ROUTES.Image_Converter} element={
              <PrivateRoute >
                <ImageConverter />
              </PrivateRoute>
            } />
            <Route path={ROUTES.MANAGE_TAGS} element={
              <PrivateRoute >
                <ManageTags />
              </PrivateRoute>
            } />

{/* ── User Management ─────────────────────────────────────────────────
    USERS list + CREATE + EDIT:
      - Client owner: always allowed
      - Child user with manage_users: allowed (Team Overview equivalent)
      - Anyone else: → /403
    Uses TeamManagementRoute (no requireClientOwner) so child users pass.
─────────────────────────────────────────────────────────────────── */}
<Route path={ROUTES.USERS} element={
  <TeamManagementRoute>
    <Users />
  </TeamManagementRoute>
} />
<Route path={ROUTES.USER_CREATE} element={
  <TeamManagementRoute>
    <UserCreate />
  </TeamManagementRoute>
} />
<Route path={ROUTES.USER_EDIT} element={
  <TeamManagementRoute>
    <UserEdit />
  </TeamManagementRoute>
} />

{/* ── Admin Panel ─────────────────────────────────────────────────────
    Dashboard, Roles, Teams, Hierarchy → CLIENT OWNER ONLY
      requireClientOwner blocks child users even if they have manage_users

    Team Overview (AdminUsers) → client owner + child users with manage_users
      no requireClientOwner so child users with manage_users can access it
─────────────────────────────────────────────────────────────────── */}

{/* Dashboard — client owner only */}
<Route path={ROUTES.ADMIN_PANEL} element={
  <TeamManagementRoute requireClientOwner>
    <OrgProvider><ClientAdminDashboard /></OrgProvider>
  </TeamManagementRoute>
} />

{/* Roles & Permissions — client owner only */}
<Route path={ROUTES.ADMIN_PANEL_ROLES} element={
  <TeamManagementRoute requireClientOwner>
    <RolesManager />
  </TeamManagementRoute>
} />

{/* Team Overview — client owner + child users with manage_users */}
<Route path={ROUTES.ADMIN_PANEL_USERS} element={
  <TeamManagementRoute>
    <AdminUsers />
  </TeamManagementRoute>
} />

{/* Team Groups — client owner only */}
<Route path={ROUTES.ADMIN_PANEL_TEAMS} element={
  <TeamManagementRoute requireClientOwner>
    <OrgProvider><TeamManagement /></OrgProvider>
  </TeamManagementRoute>
} />

{/* Org Hierarchy — client owner only */}
<Route path={ROUTES.ADMIN_PANEL_HIERARCHY || '/admin-panel/hierarchy'} element={
  <TeamManagementRoute requireClientOwner>
    <OrgProvider><HierarchyView /></OrgProvider>
  </TeamManagementRoute>
} />
            {/* ── Task Management ──────────────────────────────────────────────
                Requires: Team_Management_Permission on license only
                Bypassing by URL is blocked: TeamManagementRoute redirects to /403
            ─────────────────────────────────────────────────────────────────── */}
            <Route path={ROUTES.TASKS} element={
              <PrivateRoute requiredPermissions={["Task_Management_Permission"]}>
                <TaskBoard />
              </PrivateRoute>
            } />
            <Route path={ROUTES.TASK_REMINDERS} element={
              <PrivateRoute requiredPermissions={["Task_Management_Permission"]}>
                <RemindersListPage />
              </PrivateRoute>
            } />

            {/* Legal Drafting Routes  */}
             <Route
              path={ROUTES.LEGAL_DRAFTING}
              element={
                <PrivateRoute requiredPermissions={["can_draft_legal"]}>
                  <LegalDraftingPage />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.LEGAL_DRAFTING_CASES}
              element={
                <PrivateRoute requiredPermissions={["can_manage_cases"]}>
                  <CaseLibraryPage />
                </PrivateRoute>
              }
            />
                        {/* Brand Generator */}
            <Route path={ROUTES.BRAND_GENERATOR} element={
              <PrivateRoute requiredPermissions={["Brand_Generator_Permission"]}>
                <BrandGeneratorPage />
              </PrivateRoute>
            } />
            <Route path={ROUTES.TRADEMARK_PREDICTOR} element={
              <PrivateRoute requiredPermissions={["TM_Availability_Predictor"]}>
                <TrademarkPredictorPage />
              </PrivateRoute>
            } />
            {/* Reports Routes */}
            <Route path={ROUTES.REPORTS} element={<Reports />} />
            <Route path={ROUTES.REPORT_CREATE} element={<ReportCreate />} />
            <Route path={ROUTES.REPORT_VIEW} element={<ReportView />} />

            {/* Documents & Notes Routes */}
            <Route path={ROUTES.DOCUMENTS} element={<Documents />} />
            <Route path={ROUTES.NOTES} element={<Notes />} />

            {/* Settings Routes */}
            <Route path={ROUTES.SETTINGS} element={<Settings />} />

            <Route path={ROUTES.BILLING} element={
              <PrivateRoute allowedUserTypes={[USER_TYPES.CLIENT]} requiredPermissions={["Client_Billing_Permission"]}>
                <Billing />
              </PrivateRoute>
            } />

            <Route path={ROUTES.OPPONENT_STRENGTH} element={
              <PrivateRoute requiredPermissions={["Opponent_Strength_Permission"]}>
                <OpponentStrengthMeter />
              </PrivateRoute>
            } />

            <Route path={ROUTES.LICENSE} element={<License />} />



            {/* Support */}
            <Route path="/support" element={
              <PrivateRoute>
                <SupportPage />
              </PrivateRoute>
            } />
          </Route>

          {/* 404 Route */}
          <Route path={ROUTES.NOT_FOUND} element={<NotFound />} />
          <Route path={ROUTES.UNAUTHORIZED} element={<Unauthorized />} />
          <Route path="*" element={<Navigate to={ROUTES.NOT_FOUND} replace />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default AppRouter;